/**
 * Filter Layer Component
 * Top layer filter chips for Swirlspace discovery
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Image, FileText, Link as LinkIcon, Video, Heart, ChevronDown, Radio } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const FILTERS = [
  { label: 'Favorites', icon: Heart, value: 'favorites' },
  { label: 'Videos', icon: Video, value: 'videos' },
  { label: 'Images', icon: Image, value: 'images' },
  { label: 'Notes', icon: FileText, value: 'notes' },
  { label: 'Articles', icon: LinkIcon, value: 'articles' },
  { label: 'Books', icon: FileText, value: 'books' },
  { label: 'Podcasts', icon: Radio, value: 'podcasts' },
];

export default function FilterLayer({ onFilterClick, activeFilter, scrollToOrganizationLayer }) {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [channelsOpen, setChannelsOpen] = useState(false);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    if (user?.id) {
      loadChannels();
    }
  }, [user?.id]);

  const loadChannels = async () => {
    setLoadingChannels(true);
    try {
      // Fetch actual channels from instagram_content table (distinct channel values)
      const { data, error } = await supabase
        .schema('public')
        .from('instagram_content')
        .select('channel')
        .eq('user_id', user.id)
        .not('channel', 'is', null)
        .order('channel', { ascending: true });

      if (error) {
        if (error.code === 'PGRST116' || error.code === 'PGRST205') {
          // Table doesn't exist, try social_identities as fallback
          const { data: socialData, error: socialError } = await supabase
            .from('social_identities')
            .select('platform, handle')
            .eq('user_id', user.id)
            .order('platform', { ascending: true });

          if (!socialError && socialData) {
            const platformMap = {
              instagram: 'Instagram',
              pinterest: 'Pinterest',
              youtube: 'YouTube',
              twitter: 'Twitter',
            };

            const uniqueChannels = [...new Set((socialData || [])
              .map(item => platformMap[item.platform] || item.platform)
              .filter(Boolean))];

            setChannels(uniqueChannels.map(name => ({ name })));
            return;
          }
        } else {
          console.error('Error loading channels:', error);
        }
        setChannels([]);
        return;
      }

      // Extract unique channel names and count items per channel
      // Filter out any invalid channel names (like "Recent", "All", etc.)
      const invalidChannelNames = ['Recent', 'All', 'Collections', 'Collection'];
      
      const channelMap = new Map();
      (data || []).forEach(item => {
        if (item.channel && typeof item.channel === 'string') {
          const channelName = item.channel.trim();
          // Only add if it's a valid channel name
          if (channelName && !invalidChannelNames.includes(channelName)) {
            const count = channelMap.get(channelName) || 0;
            channelMap.set(channelName, count + 1);
          }
        }
      });

      // Convert to array sorted by name
      const uniqueChannels = Array.from(channelMap.entries())
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => a.name.localeCompare(b.name));

      setChannels(uniqueChannels);
      
      if (import.meta.env.DEV) {
        console.log('📡 Loaded channels:', uniqueChannels.map(c => `${c.name} (${c.count} items)`));
      }
    } catch (error) {
      console.error('Error loading channels:', error);
      setChannels([]);
    } finally {
      setLoadingChannels(false);
    }
  };

  const handleFilterClick = (filterValue) => {
    console.log('🔘 [FilterLayer] Button clicked:', filterValue);
    if (onFilterClick) {
      console.log('🔘 [FilterLayer] Calling onFilterClick with:', { type: 'filter', value: filterValue });
      onFilterClick({ type: 'filter', value: filterValue });
    } else {
      console.error('🔘 [FilterLayer] onFilterClick is NOT defined!');
    }
  };

  const handleChannelSelect = (channelName) => {
    if (onFilterClick) {
      onFilterClick({ type: 'channel', value: channelName });
    }
    setChannelsOpen(false);
  };

  return (
    <div className="mb-0 relative z-20">
      <div className="flex flex-wrap gap-3 items-center" style={{ minHeight: '32px' }}>

        {/* Channels Dropdown - moved next to Recent Uploads */}
        <div className="relative" ref={buttonRef}>
          <motion.button
            ref={buttonRef}
            onClick={() => setChannelsOpen(!channelsOpen)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-all ${
              channelsOpen || activeFilter?.type === 'channel'
                ? 'bg-white border-neutral-300 text-neutral-800'
                : 'bg-white border-neutral-200 text-neutral-700 hover:border-neutral-300'
            }`}
          >
            <Radio className="h-4 w-4" />
            <span>Channels</span>
            <ChevronDown 
              className={`h-4 w-4 transition-transform ${channelsOpen ? 'rotate-180' : ''}`} 
            />
          </motion.button>

          {/* Dropdown Menu - Render in portal to ensure it's above everything */}
          {typeof document !== 'undefined' && createPortal(
            <AnimatePresence>
              {channelsOpen && (
                <>
                  {/* Backdrop to close dropdown */}
                  <motion.div
                    className="fixed inset-0"
                    style={{ zIndex: 9998, backgroundColor: 'rgba(0, 0, 0, 0.05)' }}
                    onClick={() => setChannelsOpen(false)}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  />
                  <motion.div
                    ref={dropdownRef}
                    initial={{ opacity: 0, scale: 0.95, y: -5 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -5 }}
                    transition={{ duration: 0.15, ease: 'easeOut' }}
                    className="fixed bg-white/95 rounded-2xl shadow-2xl border-2 border-gray-200 min-w-[240px] max-h-[380px] overflow-y-auto"
                    style={{ 
                      position: 'fixed',
                      zIndex: 9999,
                      top: buttonRef.current ? `${buttonRef.current.getBoundingClientRect().bottom + 12}px` : 'auto',
                      left: buttonRef.current ? `${buttonRef.current.getBoundingClientRect().left}px` : 'auto',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                    }}
                  >
                {loadingChannels ? (
                  <div className="p-4 text-center text-neutral-500 text-sm">
                    Loading channels...
                  </div>
                ) : channels.length === 0 ? (
                  <div className="p-4 text-center text-neutral-500 text-sm">
                    No channels found
                    <p className="text-xs mt-1 text-neutral-400">Connect sources to see channels</p>
                  </div>
                ) : (
                  <div className="py-2">
                    <div className="px-4 py-2.5 text-xs font-semibold text-neutral-600 uppercase tracking-wider">
                      Channels ({channels.length})
                    </div>
                    <div className="px-2">
                      {channels.map((channel, index) => (
                        <button
                          key={index}
                          onClick={() => handleChannelSelect(channel.name)}
                          className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-purple-50 transition-all text-sm text-neutral-800 flex items-center justify-between group"
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{channel.name}</span>
                            {channel.count !== undefined && (
                              <span className="text-xs text-neutral-400 font-normal">({channel.count})</span>
                            )}
                          </div>
                          <span className="text-xs text-neutral-400 group-hover:text-purple-500 transition-colors">→</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                  </motion.div>
                </>
              )}
            </AnimatePresence>,
            document.body
          )}
        </div>

        {/* Standard Filters */}
        {FILTERS.map((filter) => {
          const Icon = filter.icon;
          const isActive = activeFilter === filter.value;
          
          return (
            <motion.button
              key={filter.value}
              onClick={() => handleFilterClick(filter.value)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-all ${
                isActive
                  ? 'bg-white border-neutral-300 text-neutral-800'
                  : 'bg-white border-neutral-200 text-neutral-700 hover:border-neutral-300'
              }`}
            >
              {Icon && <Icon className="h-4 w-4 flex-shrink-0" />}
              <span className="whitespace-nowrap">{filter.label}</span>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}

